import ExcelJS from 'exceljs';
import type { Quotation, Party, Company, SalesRep } from '../types';
import { logUserActivity } from './activityLogger';

export interface FinancialBranchStats {
  id?: number | string;
  code: string;
  name: string;
  count: number;
  grossSubtotal: number;
  discountAmount: number;
  pfAmount: number;
  gstAmount: number;
  netTotal: number;
  avgTicket: number;
  sharePct: number;
}

export interface MonthlyFinancialStats {
  month: string;
  count: number;
  pipelineAmount: number;
  approvedAmount: number;
  gstAmount: number;
}

export interface FinancialReportData {
  totalValue: number;
  totalSubtotal: number;
  totalDiscount: number;
  totalPF: number;
  totalGST: number;
  approvedValue: number;
  approvedCount: number;
  branches: FinancialBranchStats[];
  monthly: MonthlyFinancialStats[];
}

export interface SalesRepAnalytics {
  id?: number | string;
  code: string;
  name: string;
  role?: string;
  count: number;
  draftCount: number;
  sentCount: number;
  approvedCount: number;
  rejectedCount: number;
  totalAmount: number;
  approvedAmount: number;
  conversionRate: number;
}

export interface TopPartyAnalytics {
  rank: number;
  id?: number | string;
  name: string;
  count: number;
  amount: number;
  avgTicket: number;
  sharePct: number;
}

export interface StatusFunnelAnalytics {
  status: string;
  count: number;
  amount: number;
  percentage: number;
}

export interface AnalyticsReportData {
  reps: SalesRepAnalytics[];
  parties: TopPartyAnalytics[];
  funnel: StatusFunnelAnalytics[];
  totalValue: number;
  totalQuotes: number;
}

// ─── Helpers ───
function triggerDownload(content: string, filename: string, mimeType = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  logUserActivity({
    action_type: 'REPORT_EXPORTED',
    resource_id: filename,
    details: `Exported CSV report: ${filename}`,
  });
}

async function triggerExcelDownload(workbook: ExcelJS.Workbook, filename: string) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  logUserActivity({
    action_type: 'EXCEL_GENERATED',
    resource_id: filename,
    details: `Exported Excel report: ${filename}`,
  });
}

// ─── 1. Quotations Export ───
export function exportQuotationsCSV(
  quotations: Quotation[],
  parties: Party[],
  companies: Company[],
  reps: SalesRep[]
) {
  const headers = [
    'Quotation No',
    'Date',
    'Query Ref',
    'Query Date',
    'Company Code',
    'Company Name',
    'Sales Rep',
    'Customer Party',
    'Subtotal (INR)',
    'Discount (INR)',
    'P&F (INR)',
    'GST (INR)',
    'Grand Total (INR)',
    'Status',
  ];

  const rows = quotations.map((q) => {
    const party = parties.find((p) => p.id === q.party_id);
    const company = companies.find((c) => c.id === q.company_id);
    const rep = reps.find((r) => r.id === q.rep_id);
    return [
      `"${q.quotation_number}"`,
      `"${q.quotation_date}"`,
      `"${q.query_number || ''}"`,
      `"${q.query_date || ''}"`,
      `"${company?.code || ''}"`,
      `"${(company?.name || '').replace(/"/g, '""')}"`,
      `"${rep?.name || rep?.code || ''}"`,
      `"${(party?.name || '').replace(/"/g, '""')}"`,
      q.subtotal || 0,
      q.discount_amount || 0,
      q.pf_amount || 0,
      q.gst_amount || 0,
      q.total || 0,
      `"${q.status || 'draft'}"`,
    ].join(',');
  });

  const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
  triggerDownload(csvContent, `SKI_Quotations_Report_${new Date().toISOString().split('T')[0]}.csv`);
}

export async function exportQuotationsExcel(
  quotations: Quotation[],
  parties: Party[],
  companies: Company[],
  reps: SalesRep[]
) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sai Krishna Industries';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Quotations Report', {
    views: [{ showGridLines: true }],
  });

  sheet.columns = [
    { header: 'Quotation No', key: 'qno', width: 22 },
    { header: 'Date', key: 'qdate', width: 14 },
    { header: 'Query Ref', key: 'queryNo', width: 16 },
    { header: 'Query Date', key: 'queryDate', width: 14 },
    { header: 'Branch Code', key: 'compCode', width: 14 },
    { header: 'Branch Entity', key: 'compName', width: 28 },
    { header: 'Sales Rep', key: 'rep', width: 20 },
    { header: 'Customer Party', key: 'party', width: 34 },
    { header: 'Subtotal (₹)', key: 'subtotal', width: 16 },
    { header: 'Discount (₹)', key: 'discount', width: 14 },
    { header: 'P&F (₹)', key: 'pf', width: 12 },
    { header: 'GST (₹)', key: 'gst', width: 14 },
    { header: 'Grand Total (₹)', key: 'total', width: 18 },
    { header: 'Status', key: 'status', width: 14 },
  ];

  // Header styling
  const headerRow = sheet.getRow(1);
  headerRow.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  headerRow.height = 24;

  quotations.forEach((q) => {
    const party = parties.find((p) => p.id === q.party_id);
    const company = companies.find((c) => c.id === q.company_id);
    const rep = reps.find((r) => r.id === q.rep_id);

    sheet.addRow({
      qno: q.quotation_number,
      qdate: q.quotation_date,
      queryNo: q.query_number || '-',
      queryDate: q.query_date || '-',
      compCode: company?.code || '',
      compName: company?.name || '',
      rep: rep?.name || rep?.code || '',
      party: party?.name || '',
      subtotal: q.subtotal || 0,
      discount: q.discount_amount || 0,
      pf: q.pf_amount || 0,
      gst: q.gst_amount || 0,
      total: q.total || 0,
      status: (q.status || 'draft').toUpperCase(),
    });
  });

  // Format currency columns
  ['I', 'J', 'K', 'L', 'M'].forEach((col) => {
    sheet.getColumn(col).numFmt = '₹#,##0.00';
  });

  await triggerExcelDownload(workbook, `SKI_Quotations_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ─── 2. Financial Reports Export ───
export function exportFinancialsCSV(data: FinancialReportData) {
  const lines: string[] = [];

  // Top Title
  lines.push('"SAI KRISHNA INDUSTRIES - FINANCIAL STATEMENT & COMMERCIAL LEDGER"');
  lines.push(`"Report Generated: ${new Date().toLocaleString('en-IN')}"`);
  lines.push('');

  // KPIs
  lines.push('"CONSOLIDATED EXECUTIVE SUMMARY"');
  lines.push('"Metric","Amount (INR)"');
  lines.push(`"Total Pipeline Volume",${data.totalValue}`);
  lines.push(`"Gross Subtotal (Before Tax & Disc)",${data.totalSubtotal}`);
  lines.push(`"Total Commercial Discounts Given",${data.totalDiscount}`);
  lines.push(`"Total Packing & Forwarding (P&F)",${data.totalPF}`);
  lines.push(`"Output Tax Liability (18% GST)",${data.totalGST}`);
  lines.push(`"Approved / Realized Revenue",${data.approvedValue}`);
  lines.push(`"Approved Quotations Count",${data.approvedCount}`);
  lines.push('');

  // Branch Breakdown
  lines.push('"BRANCH & OPERATING ENTITY FINANCIAL BREAKDOWN"');
  lines.push('"Branch Code","Entity Name","Quotations Count","Gross Subtotal (INR)","Discount (INR)","P&F (INR)","Output GST (INR)","Net Total (INR)","Avg Ticket (INR)","Share (%)"');
  data.branches.forEach((b) => {
    lines.push([
      `"${b.code}"`,
      `"${b.name.replace(/"/g, '""')}"`,
      b.count,
      b.grossSubtotal,
      b.discountAmount,
      b.pfAmount,
      b.gstAmount,
      b.netTotal,
      b.avgTicket,
      `${b.sharePct.toFixed(1)}%`,
    ].join(','));
  });
  lines.push('');

  // Monthly Financial Breakdown
  lines.push('"MONTHLY FINANCIAL REVENUE DISTRIBUTION"');
  lines.push('"Month","Quotes Count","Pipeline Volume (INR)","Approved Realized (INR)","Output GST Liability (INR)"');
  data.monthly.forEach((m) => {
    lines.push([
      `"${m.month}"`,
      m.count,
      m.pipelineAmount,
      m.approvedAmount,
      m.gstAmount,
    ].join(','));
  });

  const csvContent = '\uFEFF' + lines.join('\n');
  triggerDownload(csvContent, `SKI_Financial_Report_${new Date().toISOString().split('T')[0]}.csv`);
}

export async function exportFinancialsExcel(data: FinancialReportData) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sai Krishna Industries';
  workbook.created = new Date();

  // Sheet 1: Executive Financial Summary
  const summarySheet = workbook.addWorksheet('Financial Summary', {
    views: [{ showGridLines: true }],
  });

  summarySheet.columns = [
    { width: 38 },
    { width: 24 },
  ];

  summarySheet.mergeCells('A1:B1');
  const title = summarySheet.getCell('A1');
  title.value = 'SAI KRISHNA INDUSTRIES - FINANCIAL REPORT';
  title.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
  title.alignment = { horizontal: 'center', vertical: 'middle' };
  summarySheet.getRow(1).height = 28;

  const kpis = [
    ['Total Pipeline Value', data.totalValue],
    ['Gross Commercial Subtotal', data.totalSubtotal],
    ['Commercial Discounts Conceded', data.totalDiscount],
    ['Packing & Forwarding (P&F)', data.totalPF],
    ['Output Tax Liability (18% GST)', data.totalGST],
    ['Approved / Realized Revenue', data.approvedValue],
    ['Approved Quotations Count', data.approvedCount],
  ];

  summarySheet.addRow(['Metric', 'Amount (₹)']);
  summarySheet.getRow(2).font = { bold: true };
  summarySheet.getRow(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };

  kpis.forEach(([label, val]) => {
    const row = summarySheet.addRow([label, val]);
    if (typeof val === 'number' && label !== 'Approved Quotations Count') {
      row.getCell(2).numFmt = '₹#,##0.00';
    }
  });

  // Sheet 2: Branch Financials
  const branchSheet = workbook.addWorksheet('Branch Ledger', {
    views: [{ showGridLines: true }],
  });

  branchSheet.columns = [
    { header: 'Branch Code', key: 'code', width: 14 },
    { header: 'Operating Entity', key: 'name', width: 32 },
    { header: 'Quotes', key: 'count', width: 12 },
    { header: 'Gross Subtotal (₹)', key: 'gross', width: 18 },
    { header: 'Discounts (₹)', key: 'disc', width: 16 },
    { header: 'P&F (₹)', key: 'pf', width: 14 },
    { header: 'Output GST (₹)', key: 'gst', width: 16 },
    { header: 'Net Total (₹)', key: 'net', width: 18 },
    { header: 'Avg Ticket (₹)', key: 'avg', width: 16 },
    { header: 'Share (%)', key: 'share', width: 12 },
  ];

  const bHeader = branchSheet.getRow(1);
  bHeader.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  bHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } };
  bHeader.alignment = { vertical: 'middle', horizontal: 'center' };
  bHeader.height = 24;

  data.branches.forEach((b) => {
    branchSheet.addRow({
      code: b.code,
      name: b.name,
      count: b.count,
      gross: b.grossSubtotal,
      disc: b.discountAmount,
      pf: b.pfAmount,
      gst: b.gstAmount,
      net: b.netTotal,
      avg: b.avgTicket,
      share: `${b.sharePct.toFixed(1)}%`,
    });
  });

  ['D', 'E', 'F', 'G', 'H', 'I'].forEach((col) => {
    branchSheet.getColumn(col).numFmt = '₹#,##0.00';
  });

  // Sheet 3: Monthly Distribution
  const monthSheet = workbook.addWorksheet('Monthly Financials', {
    views: [{ showGridLines: true }],
  });

  monthSheet.columns = [
    { header: 'Month', key: 'month', width: 16 },
    { header: 'Quotations Count', key: 'count', width: 18 },
    { header: 'Pipeline Volume (₹)', key: 'pipeline', width: 22 },
    { header: 'Approved Revenue (₹)', key: 'approved', width: 22 },
    { header: 'Output GST (₹)', key: 'gst', width: 18 },
  ];

  const mHeader = monthSheet.getRow(1);
  mHeader.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  mHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } };
  mHeader.alignment = { vertical: 'middle', horizontal: 'center' };
  mHeader.height = 24;

  data.monthly.forEach((m) => {
    monthSheet.addRow({
      month: m.month,
      count: m.count,
      pipeline: m.pipelineAmount,
      approved: m.approvedAmount,
      gst: m.gstAmount,
    });
  });

  ['C', 'D', 'E'].forEach((col) => {
    monthSheet.getColumn(col).numFmt = '₹#,##0.00';
  });

  await triggerExcelDownload(workbook, `SKI_Financial_Statements_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ─── 3. Analytics & Performance Export ───
export function exportAnalyticsCSV(data: AnalyticsReportData) {
  const lines: string[] = [];

  lines.push('"SAI KRISHNA INDUSTRIES - SALES ANALYTICS & EXECUTIVE LEADERBOARD"');
  lines.push(`"Report Generated: ${new Date().toLocaleString('en-IN')}"`);
  lines.push('');

  // Reps
  lines.push('"SALES REPRESENTATIVE PERFORMANCE & CONVERSION LEADERBOARD"');
  lines.push('"Rep Code","Executive Name","Role","Total Quotes","Drafted","Sent","Approved","Rejected","Total Pipeline (INR)","Approved Revenue (INR)","Conversion Win Rate (%)"');
  data.reps.forEach((r) => {
    lines.push([
      `"${r.code}"`,
      `"${r.name.replace(/"/g, '""')}"`,
      `"${r.role || 'Sales'}"`,
      r.count,
      r.draftCount,
      r.sentCount,
      r.approvedCount,
      r.rejectedCount,
      r.totalAmount,
      r.approvedAmount,
      `${r.conversionRate.toFixed(1)}%`,
    ].join(','));
  });
  lines.push('');

  // Top Customers
  lines.push('"TOP CUSTOMER ACCOUNTS CONCENTRATION"');
  lines.push('"Rank","Customer Name","Quotations Count","Total Volume (INR)","Average Order (INR)","Share of Business (%)"');
  data.parties.forEach((p) => {
    lines.push([
      `#${p.rank}`,
      `"${p.name.replace(/"/g, '""')}"`,
      p.count,
      p.amount,
      p.avgTicket,
      `${p.sharePct.toFixed(1)}%`,
    ].join(','));
  });
  lines.push('');

  // Funnel
  lines.push('"QUOTATION STATUS PIPELINE FUNNEL"');
  lines.push('"Status","Count","Total Volume (INR)","Percentage (%)"');
  data.funnel.forEach((f) => {
    lines.push([
      `"${f.status.toUpperCase()}"`,
      f.count,
      f.amount,
      `${f.percentage.toFixed(1)}%`,
    ].join(','));
  });

  const csvContent = '\uFEFF' + lines.join('\n');
  triggerDownload(csvContent, `SKI_Analytics_Report_${new Date().toISOString().split('T')[0]}.csv`);
}

export async function exportAnalyticsExcel(data: AnalyticsReportData) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sai Krishna Industries';
  workbook.created = new Date();

  // Sheet 1: Sales Reps
  const repSheet = workbook.addWorksheet('Sales Rep Leaderboard', {
    views: [{ showGridLines: true }],
  });

  repSheet.columns = [
    { header: 'Code', key: 'code', width: 10 },
    { header: 'Representative Name', key: 'name', width: 26 },
    { header: 'Role', key: 'role', width: 14 },
    { header: 'Total Quotes', key: 'count', width: 13 },
    { header: 'Drafted', key: 'draftCount', width: 11 },
    { header: 'Sent', key: 'sentCount', width: 11 },
    { header: 'Approved', key: 'appCount', width: 12 },
    { header: 'Rejected', key: 'rejcCount', width: 11 },
    { header: 'Pipeline Value (₹)', key: 'pipeline', width: 20 },
    { header: 'Approved Value (₹)', key: 'approved', width: 20 },
    { header: 'Win Rate (%)', key: 'winRate', width: 14 },
  ];

  const rHeader = repSheet.getRow(1);
  rHeader.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  rHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
  rHeader.alignment = { vertical: 'middle', horizontal: 'center' };
  rHeader.height = 24;

  data.reps.forEach((r) => {
    repSheet.addRow({
      code: r.code,
      name: r.name,
      role: r.role || 'Sales',
      count: r.count,
      draftCount: r.draftCount,
      sentCount: r.sentCount,
      appCount: r.approvedCount,
      rejcCount: r.rejectedCount,
      pipeline: r.totalAmount,
      approved: r.approvedAmount,
      winRate: `${r.conversionRate.toFixed(1)}%`,
    });
  });

  ['I', 'J'].forEach((col) => {
    repSheet.getColumn(col).numFmt = '₹#,##0.00';
  });

  // Sheet 2: Top Customers
  const custSheet = workbook.addWorksheet('Top Accounts', {
    views: [{ showGridLines: true }],
  });

  custSheet.columns = [
    { header: 'Rank', key: 'rank', width: 10 },
    { header: 'Customer Party Name', key: 'name', width: 34 },
    { header: 'Quotations Count', key: 'count', width: 18 },
    { header: 'Total Volume (₹)', key: 'amount', width: 20 },
    { header: 'Avg Ticket (₹)', key: 'avg', width: 18 },
    { header: 'Share (%)', key: 'share', width: 14 },
  ];

  const cHeader = custSheet.getRow(1);
  cHeader.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  cHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7C3AED' } };
  cHeader.alignment = { vertical: 'middle', horizontal: 'center' };
  cHeader.height = 24;

  data.parties.forEach((p) => {
    custSheet.addRow({
      rank: `#${p.rank}`,
      name: p.name,
      count: p.count,
      amount: p.amount,
      avg: p.avgTicket,
      share: `${p.sharePct.toFixed(1)}%`,
    });
  });

  ['D', 'E'].forEach((col) => {
    custSheet.getColumn(col).numFmt = '₹#,##0.00';
  });

  // Sheet 3: Funnel
  const funnelSheet = workbook.addWorksheet('Status Funnel', {
    views: [{ showGridLines: true }],
  });

  funnelSheet.columns = [
    { header: 'Quotation Status', key: 'status', width: 18 },
    { header: 'Count', key: 'count', width: 14 },
    { header: 'Total Value (₹)', key: 'amount', width: 22 },
    { header: 'Share of Pipeline', key: 'pct', width: 18 },
  ];

  const fHeader = funnelSheet.getRow(1);
  fHeader.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  fHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } };
  fHeader.alignment = { vertical: 'middle', horizontal: 'center' };
  fHeader.height = 24;

  data.funnel.forEach((f) => {
    funnelSheet.addRow({
      status: f.status.toUpperCase(),
      count: f.count,
      amount: f.amount,
      pct: `${f.percentage.toFixed(1)}%`,
    });
  });

  funnelSheet.getColumn('C').numFmt = '₹#,##0.00';

  await triggerExcelDownload(workbook, `SKI_Analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
}
